import { DETAILED_UPLOAD_LIMIT, BULK_UPLOAD_BATCH_SIZE } from '~src/config';
import { FileX } from '~src/types/upload';
import { DbFolder } from '~src/types/db';

export interface BulkUploadSummary {
  totalFiles: number;
  totalSize: number;
  nestedFilesCount: number;
  uniqueFolderPathsCount: number;
  rootFoldersCount: number;
}

export interface BulkFolderPathEntry {
  path: string;
  segments: string[];
  depth: number;
}

export type BulkFolderLevels = BulkFolderPathEntry[][];

export interface BulkFolderEntry {
  dbFolder: DbFolder;
  isNew: boolean;
  path: string;
}

export interface CreateBulkFoldersOptions {
  files: FileX[];
  currentFolder?: DbFolder | null;
  existingFolders?: DbFolder[];
  batchSize?: number;
  createFolder: (args: {
    name: string;
    parentId: number | null;
  }) => Promise<DbFolder>;
  onBatchProgress?: (args: {
    processed: number;
    total: number;
    currentLevel: number;
    totalLevels: number;
  }) => void;
}

// Extracts the folder path from a file's webkitRelativePath.
export const getRelativeFolderPath = (file: FileX) => {
  if (!file.webkitRelativePath || file.webkitRelativePath === file.name) {
    return '';
  }

  return file.webkitRelativePath.endsWith(file.name)
    ? file.webkitRelativePath.slice(0, file.webkitRelativePath.lastIndexOf('/'))
    : file.webkitRelativePath;
};

// Checks if the upload should switch from detailed mode to bulk mode.
export const shouldUseBulkUploadMode = (files: FileX[]) => {
  return files.length > DETAILED_UPLOAD_LIMIT;
};

// Builds summary data used by the bulk upload panel.
export const buildBulkUploadSummary = (files: FileX[]): BulkUploadSummary => {
  const folderPaths = new Set<string>();
  const rootFolders = new Set<string>();

  let totalSize = 0;
  let nestedFilesCount = 0;

  files.forEach((file) => {
    totalSize += file.size || 0;

    const folderPath = getRelativeFolderPath(file);
    if (!folderPath) return;

    nestedFilesCount += 1;
    folderPaths.add(folderPath);

    const rootFolder = folderPath.split('/').filter(Boolean)[0];
    if (rootFolder) {
      rootFolders.add(rootFolder);
    }
  });

  return {
    totalFiles: files.length,
    totalSize,
    nestedFilesCount,
    uniqueFolderPathsCount: folderPaths.size,
    rootFoldersCount: rootFolders.size,
  };
};

// Collects unique nested folder paths from uploaded files.
export const extractBulkFolderPaths = (files: FileX[]): string[] => {
  return Array.from(
    new Set(
      files
        .map((file) => getRelativeFolderPath(file))
        .filter((path): path is string => !!path)
    )
  );
};

// Expands a folder path into all parent paths required for creation.
export const expandFolderPathWithParents = (path: string): string[] => {
  const segments = path.split('/').filter(Boolean);
  const result: string[] = [];

  for (let i = 1; i <= segments.length; i += 1) {
    result.push(segments.slice(0, i).join('/'));
  }

  return result;
};

// Builds sorted folder path entries with depth information.
export const buildBulkFolderEntries = (
  files: FileX[]
): BulkFolderPathEntry[] => {
  const allPaths = new Set<string>();

  extractBulkFolderPaths(files).forEach((path) => {
    expandFolderPathWithParents(path).forEach((expandedPath) => {
      allPaths.add(expandedPath);
    });
  });

  return Array.from(allPaths)
    .map((path) => {
      const segments = path.split('/').filter(Boolean);

      return {
        path,
        segments,
        depth: segments.length,
      };
    })
    .sort((a, b) => {
      if (a.depth !== b.depth) {
        return a.depth - b.depth;
      }

      return a.path.localeCompare(b.path);
    });
};

// Groups folder entries by depth so parent folders are created before children.
export const buildBulkFolderLevels = (files: FileX[]): BulkFolderLevels => {
  const entries = buildBulkFolderEntries(files);

  const levelsMap = new Map<number, BulkFolderPathEntry[]>();

  entries.forEach((entry) => {
    const current = levelsMap.get(entry.depth) || [];
    current.push(entry);
    levelsMap.set(entry.depth, current);
  });

  return Array.from(levelsMap.entries())
    .sort(([aDepth], [bDepth]) => aDepth - bDepth)
    .map(([, entriesAtDepth]) => entriesAtDepth);
};

// Splits an array into smaller batches.
export const chunkArray = <T>(items: T[], chunkSize: number): T[][] => {
  if (chunkSize <= 0) return [items];

  const chunks: T[][] = [];

  for (let i = 0; i < items.length; i += chunkSize) {
    chunks.push(items.slice(i, i + chunkSize));
  }

  return chunks;
};

// Finds the target folder entry for a specific uploaded file.
export const findFolderForFile = (
  file: FileX,
  folderList: BulkFolderEntry[],
  currentFolder?: DbFolder | null
): BulkFolderEntry | undefined => {
  const folderPath = getRelativeFolderPath(file);

  if (!folderPath) {
    return currentFolder
      ? {
          dbFolder: currentFolder,
          isNew: false,
          path: '',
        }
      : undefined;
  }

  return folderList.find((folder) => folder.path === folderPath);
};

// Looks up an existing folder by name and parent id.
const findExistingFolder = ({
  name,
  parentId,
  existingFolders,
}: {
  name: string;
  parentId: number | null;
  existingFolders: DbFolder[];
}) => {
  return existingFolders.find(
    (folder) => folder.parent_id === parentId && folder.name === name
  );
};

// Creates missing folder structure in depth-based batches.
export const createBulkFoldersInBatches = async ({
  files,
  currentFolder,
  existingFolders = [],
  batchSize = BULK_UPLOAD_BATCH_SIZE,
  createFolder,
  onBatchProgress,
}: CreateBulkFoldersOptions): Promise<BulkFolderEntry[]> => {
  const levels = buildBulkFolderLevels(files);

  if (!levels.length) {
    return [];
  }

  const folderList: BulkFolderEntry[] = [];
  const createdFoldersForLookup: DbFolder[] = [...existingFolders];

  let processed = 0;
  const total = levels.reduce((acc, level) => acc + level.length, 0);

  for (let levelIndex = 0; levelIndex < levels.length; levelIndex += 1) {
    const level = levels[levelIndex];
    const levelChunks = chunkArray(level, batchSize);

    for (const chunk of levelChunks) {
      const chunkResults = await Promise.all(
        chunk.map(async (entry) => {
          const folderName = entry.segments[entry.segments.length - 1];
          const parentPath =
            entry.segments.length > 1
              ? entry.segments.slice(0, -1).join('/')
              : '';

          const parentFolder = parentPath
            ? folderList.find((folder) => folder.path === parentPath)
            : currentFolder
            ? {
                dbFolder: currentFolder,
                isNew: false,
                path: '',
              }
            : undefined;

          const parentId = parentFolder?.dbFolder.id || null;

          const existingMatch = findExistingFolder({
            name: folderName,
            parentId,
            existingFolders: createdFoldersForLookup,
          });

          if (existingMatch) {
            return {
              dbFolder: existingMatch,
              isNew: false,
              path: entry.path,
            };
          }

          const createdFolder = await createFolder({
            name: folderName,
            parentId,
          });

          createdFoldersForLookup.push(createdFolder);

          return {
            dbFolder: createdFolder,
            isNew: true,
            path: entry.path,
          };
        })
      );

      folderList.push(...chunkResults);
      processed += chunk.length;

      if (onBatchProgress) {
        onBatchProgress({
          processed,
          total,
          currentLevel: levelIndex + 1,
          totalLevels: levels.length,
        });
      }
    }
  }

  return folderList;
};
